import React, { useState, useEffect } from "react";
import { useDrop } from "react-dnd";
import ChecklistForm from "./CheckListForm";
import CheckListEditor from "./CheckListEditor";
import ChecklistCopyModal from "./ChecklistCopyModal";
import ChecklistCreateModal from "./ChecklistCreateModal";
import styles from "./Checklists.module.css";
import { getChecklists } from "../../api/checklists";
import { updateChecklist as serviceUpdateChecklist, updateChecklistPosition as serviceUpdateChecklistPosition } from "../../services/settingsService";
import ChecklistInListItem from "./ChecklistInListItem";
import ChecklistFilters from "./ChecklistFilters";
import { useEditingChecklistState } from "../../state/editingChecklistState";
import { useSettingsState } from "../../state/settingsState";
import { Typography, Button, Card, Space, notification, Drawer, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const { Title } = Typography;

const DropList = ({ children, rootCount, onDropEnd, disabled, checklists = [] }) => {
  const [, drop] = useDrop({
    accept: "CHECKLIST",
    drop: (item, monitor) => {
      console.log('DropList drop triggered for item:', item, 'disabled:', disabled, 'didDrop:', monitor.didDrop());
      const didDropOnChild = monitor.didDrop();
      if (!didDropOnChild && item.parent === null && !disabled) {
        
        const sortedRootChecklists = checklists
          .filter(cl => !cl.parent)
          .sort((a, b) => a.position - b.position);
        
        const newPosition = sortedRootChecklists.length;
        console.log('DropList calling onDropEnd for', item.id, 'newPos', newPosition);
        
        if (item.originalPosition !== newPosition) {
          onDropEnd(item.id, newPosition);
        }
      }
    },
    canDrop: (item, monitor) => !disabled
  });
  return <div ref={drop} className={styles.checklistTree}>{children}</div>;
};

const Checklists = () => {
  const { 
    fields, 
    checklists, 
    setChecklists, 
    selectedStatus,
    handleDeleteChecklist,
    openCopyModal
  } = useSettingsState();
  
  const { editingChecklist, setEditingChecklist } = useEditingChecklistState();
  const [creatingChecklistEnabled, setCreatingChecklistEnabled] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [preselectedParent, setPreselectedParent] = useState(null);
  const [cfsInStatus, setCfsInStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('name');
  const [filteredChecklists, setFilteredChecklists] = useState([]);

  const rootChecklists = Array.isArray(checklists) ? checklists.filter(cl => !cl.parent) : [];

  useEffect(() => {
    const newCfsInStatus = Array.isArray(checklists) 
      ? checklists.map((checklist) => checklist.custom_fields?.map((cf) => cf)).flat().filter(Boolean)
      : [];
    setCfsInStatus(newCfsInStatus);
  }, [checklists]);

  useEffect(() => {
    if (editingChecklist && Array.isArray(checklists) && !checklists.map((checklist) => checklist.id).includes(editingChecklist.id)) {
      setEditingChecklist(null);
    }
  }, [checklists, editingChecklist, setEditingChecklist]);

  useEffect(() => {
    console.log('Checklists state updated:', checklists.map(c => c.id));
  }, [checklists]);

  
  useEffect(() => {
    if (!Array.isArray(checklists)) {
      setFilteredChecklists([]);
      return;
    }

    let filtered = checklists.filter(checklist => !checklist.parent);

    
    if (filters.search) {
      filtered = filtered.filter(checklist =>
        checklist.name.toLowerCase().includes(filters.search.toLowerCase())
      );
    }



    
    if (filters.hasCustomFields) {
      filtered = filtered.filter(checklist => 
        checklist.custom_fields?.length > 0
      );
    }

    if (filters.hasAmoFields) {
      filtered = filtered.filter(checklist => 
        checklist.fields_ids?.length > 0
      );
    }

    if (filters.hasSyncedFields) {
      filtered = filtered.filter(checklist => 
        checklist.custom_fields?.some(cf => cf.type !== 'C' && cf.name)
      );
    }

    
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'fieldCount':
          const aCount = (a.custom_fields?.length || 0) + (a.fields_ids?.length || 0);
          const bCount = (b.custom_fields?.length || 0) + (b.fields_ids?.length || 0);
          return bCount - aCount;

        case 'created':
          return a.position - b.position; 
        default:
          return 0;
      }
    });

    setFilteredChecklists(filtered);
  }, [checklists, filters, sortBy]);

  const handleSaveChecklist = async (updatedChecklist = null, lastChecklists = null) => {
    if (updatedChecklist && updatedChecklist.nativeEvent) {
      updatedChecklist = null;
    }
    
    if (!lastChecklists) {
      lastChecklists = [...checklists];
    }

    if (!updatedChecklist) {
      updatedChecklist = editingChecklist;
    }
    
    if (Array.isArray(checklists)) {
      setChecklists(
        checklists.map((cl) =>
          cl.id === updatedChecklist.id ? updatedChecklist : cl
        )
      );
    }
    
    setIsLoading(true);
    try {
      const res = await serviceUpdateChecklist(selectedStatus.amo_status_id, updatedChecklist.id, updatedChecklist);
      if (res.error) {
        throw new Error('Не удалось обновить чеклист');
      }
      
      const updatedChecklists = await getChecklists(selectedStatus.amo_status_id);
      setChecklists(updatedChecklists);
      notification.success({
        message: 'Успех',
        description: 'Чеклист успешно сохранен',
        placement: 'bottomRight'
      });
    } catch (error) {
      setChecklists(lastChecklists);
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось сохранить чеклист',
        placement: 'bottomRight'
      });
    } finally {
      setIsLoading(false);
      setEditingChecklist(null);
    }
  };

  const handleMoveChecklist = (id, newPosition) => {
  };
  
  const handleDragEnd = async (draggedId, targetIndex) => {
    console.log('handleDragEnd start', { draggedId, targetIndex });
    
    
    const hasActiveFilters = Object.keys(filters).length > 0 || sortBy !== 'name';
    if (hasActiveFilters) {
      notification.warning({
        message: 'Перемещение недоступно',
        description: 'Отключите фильтры и сортировку для перемещения чеклистов',
        placement: 'bottomRight'
      });
      return;
    }
    
    setIsLoading(true);
    setIsDragging(true);
    
    try {
      
      const targetChecklist = filteredChecklists[targetIndex];
      if (!targetChecklist) {
        throw new Error('Целевая позиция не найдена');
      }
      
      console.log('calling updateChecklistPosition with', selectedStatus.amo_status_id, draggedId, targetChecklist.position);
      const res = await serviceUpdateChecklistPosition(selectedStatus.amo_status_id, draggedId, targetChecklist.position);
      console.log('updateChecklistPosition response', res);
      
      if (res.error) {
        throw new Error('Не удалось обновить позицию чеклиста');
      }
      
      console.log('fetching fresh checklists from server');
      const freshChecklists = await getChecklists(selectedStatus.amo_status_id);
      console.log('fetched freshChecklists', freshChecklists.map(c => ({ id: c.id, position: c.position })));
      setChecklists(freshChecklists);

      notification.success({
        message: 'Успех',
        description: 'Позиция чеклиста успешно изменена',
        placement: 'bottomRight'
      });

    } catch (error) {
      console.error('handleDragEnd error', error);
      notification.error({
        message: 'Ошибка',
        description: error.message || 'Не удалось изменить позицию чеклиста',
        placement: 'bottomRight'
      });
    } finally {
      setIsLoading(false);
      setIsDragging(false);
    }
  };

  
  const handleCreateModalOpen = (parentId = null) => {
    setPreselectedParent(parentId);
    setCreateModalVisible(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalVisible(false);
    setPreselectedParent(null);
  };

  const handleCreateModalSuccess = async (updatedChecklists) => {
    setChecklists(updatedChecklists);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Чеклисты</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => handleCreateModalOpen()}
          disabled={isLoading}
        >
          Создать чеклист
        </Button>
      </Space>
      
      <ChecklistFilters
        checklists={filteredChecklists}
        onFilter={setFilters}
        onSort={setSortBy}
        searchValue={filters.search || ''}
        filters={filters}
        sortBy={sortBy}
      />
      
      {creatingChecklistEnabled && <ChecklistForm />}
      
      <Spin spinning={isLoading}>
        <DropList 
          rootCount={rootChecklists.length} 
          onDropEnd={handleDragEnd} 
          disabled={isLoading}
          checklists={checklists}
        >
          {filteredChecklists.map((checklist, index) => (
            <ChecklistInListItem
              key={checklist.id}
              checklist={checklist}
              index={index}
              onMove={handleMoveChecklist}
              onDragEnd={handleDragEnd}
              isProcessing={isLoading}
              onCreateSubchecklist={handleCreateModalOpen}
            />
          ))}
        </DropList>
      </Spin>
    
      <Drawer
        title={`Редактирование чеклиста "${editingChecklist?.name}"`}
        open={!!editingChecklist}
        onClose={() => setEditingChecklist(null)}
        width={850}
        destroyOnClose
        style={{ height: '100vh', top: 0 }}
        drawerStyle={{ height: '100%', border: 'none', boxShadow: 'none' }}
        headerStyle={{ borderBottom: 'none' }}
        bodyStyle={{ padding: 0, height: 'calc(100% - 55px)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {editingChecklist && (
            <CheckListEditor 
              cfsInStatus={cfsInStatus}
              onUpdateChecklistName={(name) => {
                setEditingChecklist({ ...editingChecklist, name });
              }}
              onToggleField={(id) => {
                const { fields_ids } = editingChecklist;
                if (fields_ids.includes(id)) {
                  setEditingChecklist({ ...editingChecklist, fields_ids: fields_ids.filter((fieldId) => fieldId !== id) });
                } else {
                  setEditingChecklist({ ...editingChecklist, fields_ids: [...fields_ids, id] });
                }
              }}
              onSave={handleSaveChecklist} 
            />
          )}
        </div>
      </Drawer>
      
      <ChecklistCopyModal />
      
      <ChecklistCreateModal
        visible={createModalVisible}
        onClose={handleCreateModalClose}
        onSuccess={handleCreateModalSuccess}
        statusId={selectedStatus?.amo_status_id}
        checklists={checklists}
        preselectedParent={preselectedParent}
      />
    </Space>
  );
};

export default Checklists;
